// ============================================================================
// MarketOperatorDisputesTab — ОЧЕРЕДЬ СПОРОВ оператора.
//
// Открытые/решённые — теги заявки (lib/marketDispute). Живое обновление —
// события шины (изменения заявок и сообщения обсуждений), с задержкой-склейкой;
// без таймеров-поллинга. Подробности — MarketDisputeView (общий с участником).
// ============================================================================

import { useCallback, useEffect, useRef, useState } from 'react';
import { CheckCircle2, RefreshCw, Scale } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { TabBar } from '@/components/ui/TabBar';
import { eventBusHub } from '@/lib/eventBusHub';
import { friendlyError, logErrorToJournal } from '@/lib/errors';
import { marketRequestTitle } from '@/lib/market';
import type { MarketOperatorRequest } from '@/lib/marketOperator';
import { loadMarketDisputeQueue, type MarketDisputeQueueItem } from '@/lib/marketDispute';
import { MarketDisputeCard } from './MarketDisputeCard';
import { MarketDisputeView } from './MarketDisputeView';
import { MarketEmpty, MarketError, MarketLoading } from './MarketState';

interface MarketOperatorDisputesTabProps {
  query: string;
}

type QueueStatus = 'open' | 'closed';

export function MarketOperatorDisputesTab({ query }: MarketOperatorDisputesTabProps) {
  const [status, setStatus] = useState<QueueStatus>('open');
  const [items, setItems] = useState<MarketDisputeQueueItem[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [failure, setFailure] = useState('');
  const [tick, setTick] = useState(0);
  const [active, setActive] = useState<MarketOperatorRequest | null>(null);

  const load = useCallback(async () => {
    setFailure('');
    try {
      setItems(await loadMarketDisputeQueue(status));
    } catch (e) {
      logErrorToJournal(e, 'Оператор маркетплейса: очередь споров');
      setFailure(friendlyError(e) || 'Не удалось загрузить очередь споров');
    } finally {
      setLoading(false);
    }
  }, [status]);

  useEffect(() => {
    setLoading(true);
    setItems(null);
    void load();
  }, [load, tick]);

  // Живое обновление очереди (без поллинга): новые сообщения обсуждений и
  // изменения заявок. Склейка 1.5 с — при активном чате событий много.
  const timerRef = useRef<number | null>(null);
  useEffect(() => {
    if (active) return;
    const unsubscribe = eventBusHub.subscribe((ev) => {
      const relevant = ev.topic.startsWith('data.document.mkt_requests.')
        || ev.topic.startsWith('chat.message.created.')
        || ev.topic.startsWith('chat.message.edited.');
      if (!relevant) return;
      if (timerRef.current !== null) window.clearTimeout(timerRef.current);
      timerRef.current = window.setTimeout(() => {
        timerRef.current = null;
        setTick((n) => n + 1);
      }, 1500);
    });
    return () => {
      unsubscribe();
      if (timerRef.current !== null) {
        window.clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [active]);

  if (active) {
    return (
      <MarketDisputeView
        requestId={active.id}
        requestTitle={marketRequestTitle(active)}
        mode="operator"
        onBack={() => {
          setActive(null);
          setTick((n) => n + 1);
        }}
        onResolved={() => setTick((n) => n + 1)}
      />
    );
  }

  const normalizedQuery = query.trim().toLowerCase();
  const visible = (items || []).filter(({ request }) => {
    if (!normalizedQuery) return true;
    return [request.number, request.descr, request.address, request.masterName, request.categoryName, request.statusLabel]
      .some((value) => String(value || '').toLowerCase().includes(normalizedQuery));
  });

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <div className="min-w-0 flex-1">
          <TabBar
            tabs={[
              { id: 'open', label: `Открытые${items && status === 'open' ? ` · ${items.length}` : ''}` },
              { id: 'closed', label: 'Решённые' },
            ]}
            active={status}
            group="market-disputes"
            onChange={(id) => setStatus(id as QueueStatus)}
          />
        </div>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-8 w-8 shrink-0 rounded-lg p-0"
          onClick={() => setTick((n) => n + 1)}
          disabled={loading}
          aria-label="Обновить очередь споров"
        >
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
        </Button>
      </div>

      {failure ? (
        <MarketError failure={{ kind: 'other', message: failure }} onRetry={() => setTick((n) => n + 1)} />
      ) : loading && items === null ? (
        <MarketLoading text="Загружаем споры…" />
      ) : visible.length === 0 ? (
        <MarketEmpty
          icon={status === 'open' ? <Scale className="h-5 w-5" /> : <CheckCircle2 className="h-5 w-5" />}
          title={(items?.length || 0) > 0 ? 'Ничего не найдено' : status === 'open' ? 'Открытых споров нет' : 'Решённых споров нет'}
          text={(items?.length || 0) > 0
            ? 'Измените запрос — поиск идёт по номеру, описанию, адресу, мастеру и категории.'
            : status === 'open'
              ? 'Спор открывает заказчик или мастер заявки. Он появится здесь автоматически.'
              : 'После решения оператора спор переходит в этот список; история сохраняется.'}
        />
      ) : (
        <div className="space-y-2.5">
          {visible.map(({ request, status: itemStatus }) => (
            <MarketDisputeCard
              key={request.id}
              request={request}
              status={itemStatus}
              onOpen={(r) => setActive(r)}
            />
          ))}
        </div>
      )}
    </div>
  );
}
